/**
 * Does this page send the visitor to the staging login (/login/)?
 *
 * The staging and mock builds (GATSBY_ENVIRONMENT=DEV) put every page behind a
 * client-side username and password; production has no gate. Called from
 * gatsby-browser.js onRouteUpdate.
 *
 * ONE EXCEPTION, AND ONLY ON THE MOCK SITE: /switch/preview/
 * (src/mock/preview.ts). The rep portal frames it to show a rep the screen a
 * customer is on, reps do not have the staging password, and the page is
 * sample data with no customer behind it. It also keeps no storage (it swaps
 * localStorage for an in-memory one), so a signed-in visitor would be sent to
 * the login anyway. The exemption needs `mockMode`, which gatsby-browser.js
 * passes as the literal build flag, so a staging build gates the path like any
 * other (and has no such page).
 */
export interface StagingGateFacts {
  environment: string | undefined
  mockMode: boolean
  pathname: string
  /** The `authFlag` localStorage value, raw. */
  session: string | null
  now: number
}

const PREVIEW_PATH = '/switch/preview'

function validSession(raw: string | null, now: number): boolean {
  if (!raw) return false
  try {
    const session = JSON.parse(raw) as { valid?: unknown; expires?: unknown } | null
    return Boolean(session && session.valid && typeof session.expires === 'number' && now < session.expires)
  } catch {
    return false
  }
}

export function needsStagingLogin({ environment, mockMode, pathname, session, now }: StagingGateFacts): boolean {
  if (environment !== 'DEV') return false
  // trailingSlash: 'always' makes the pathname "/login/"; compare without it,
  // or the login page redirects to itself for ever.
  const path = pathname.replace(/\/+$/, '')
  if (path === '/login') return false
  if (mockMode && path === PREVIEW_PATH) return false
  return !validSession(session, now)
}
